import React, { useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import type { OptionProps, StylesConfig } from "react-select";
import { searchUsers } from "../api/UserApi";
import { UserIcon } from "lucide-react";

export interface JiraUserOption {
  value: string; // accountId
  label: string; // displayName
  avatarUrl?: string;
  accountId: string;
}

export interface JiraUserPickerProps {
  value?: JiraUserOption | JiraUserOption[] | null;
  onChange: (users: JiraUserOption[]) => void;
  placeholder?: string;
  className?: string;
  isMulti?: boolean;
  excludeAccountIds?: string[]; // Account IDs to exclude from results
}

const styles: StylesConfig<JiraUserOption, boolean> = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,0.1)" : "none",
    ":hover": { borderColor: state.isFocused ? "#2563eb" : "#d1d5db" },
    fontSize: 14,
  }),
  valueContainer: (b) => ({ ...b, padding: "2px 8px" }),
  indicatorsContainer: (b) => ({ ...b, height: 40 }),
  dropdownIndicator: (b) => ({ ...b, padding: 4 }),
  clearIndicator: (b) => ({ ...b, padding: 4 }),
  option: (b, s) => ({
    ...b,
    padding: "8px 12px",
    fontSize: 14,
    backgroundColor: s.isFocused ? "#f3f4f6" : s.isSelected ? "#dbeafe" : undefined,
    color: "#111827",
  }),
  menu: (b) => ({ ...b, zIndex: 30, borderRadius: 8 }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  placeholder: (b) => ({ ...b, color: "#6b7280", fontSize: 14 }),
  singleValue: (b) => ({ ...b, fontSize: 14, color: "#1f2937" }),
  multiValue: (b) => ({
    ...b,
    backgroundColor: "#eff6ff",
    borderRadius: 6,
  }),
  multiValueLabel: (b) => ({
    ...b,
    color: "#1f2937",
    fontWeight: 500,
  }),
  multiValueRemove: (b) => ({
    ...b,
    ":hover": {
      backgroundColor: "transparent",
      color: "#ef4444",
    },
  }),
};

const JiraUserPicker: React.FC<JiraUserPickerProps> = ({
  value,
  onChange,
  placeholder = "Search Jira users...",
  className = "",
  isMulti = true,
  excludeAccountIds = [],
}) => {
  const [, setInputValue] = useState<string>("");

  const loadOptions = async (inputValue: string): Promise<JiraUserOption[]> => {
    if (!inputValue || inputValue.trim().length < 2) {
      return [];
    }
    try {
      const users = await searchUsers(inputValue.trim());
      return users
        .filter((user: any) => !excludeAccountIds.includes(user.accountId))
        .map((user: any) => ({
          value: user.accountId,
          label: user.displayName,
          avatarUrl: user.avatarUrl,
          accountId: user.accountId,
        }));
    } catch (error) {
      console.error("Error searching Jira users:", error);
      return [];
    }
  };

  const valueOptions: JiraUserOption[] = useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [value];
  }, [value]);

  const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;

  const Option = (props: OptionProps<JiraUserOption>) => {
    const { data } = props;
    return (
      <components.Option {...props}>
        <div className="w-full flex items-center gap-2">
          {data.avatarUrl ? (
            <img
              src={data.avatarUrl}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              alt={data.label}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
              <UserIcon className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{props.children}</div>
          </div>
        </div>
      </components.Option>
    );
  };

  if (isMulti) {
    return (
      <div className={className}>
        <AsyncSelect<JiraUserOption, true>
          cacheOptions
          defaultOptions={false}
          isClearable
          isMulti={true}
          loadOptions={loadOptions}
          value={valueOptions}
          onChange={(selected) => {
            if (!selected) {
              onChange([]);
              return;
            }
            if (Array.isArray(selected)) {
              onChange(selected);
            }
          }}
          placeholder={placeholder}
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          className="jira-user-picker"
          classNamePrefix="jira-user-picker"
          styles={styles}
          noOptionsMessage={({ inputValue }) =>
            inputValue.length < 2 ? "Type at least 2 characters to search" : "No users found"
          }
          loadingMessage={() => "Searching users..."}
          components={{ Option }}
          onInputChange={(val) => {
            setInputValue(val);
            return val;
          }}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <AsyncSelect<JiraUserOption, false>
        cacheOptions
        defaultOptions={false}
        isClearable
        isMulti={false}
        loadOptions={loadOptions}
        value={valueOptions[0] || null}
        onChange={(selected) => {
          if (!selected) {
            onChange([]);
            return;
          }
          onChange([selected]);
        }}
        placeholder={placeholder}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        className="jira-user-picker"
        classNamePrefix="jira-user-picker"
        styles={styles}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2 ? "Type at least 2 characters to search" : "No users found"
        }
        loadingMessage={() => "Searching users..."}
        components={{ Option }}
        onInputChange={(val) => {
          setInputValue(val);
          return val;
        }}
      />
    </div>
  );
};

export default JiraUserPicker;
